import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Tab, Tabs } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import { AppDialog, EmptyState, PageHeader, Panel, StatusChip } from '../ui';
import { aggregate } from '../../lib/mockupLogsDashboard/aggregate';
import { cacheToSortedArray, fetchMissingForFilter } from '../../lib/mockupLogsDashboard/fetchCatalog';
import type { IDailyStat, IFilterState } from '../../lib/mockupLogsDashboard/types';
import {
  clampFilterRange,
  default7dFilter,
  MOCKUP_LOGS_MIN_DATE,
  prevPeriod,
  readFilterFromSearch,
  writeFilterToSearch,
} from '../../lib/mockupLogsDashboard/utils';
import { dashboardCssVars, dashboardPalette } from '../../lib/mockupLogsDashboard/palette';
import MockupLogsFilterBar from './MockupLogsFilterBar';
import MockupLogsPerformance from './MockupLogsPerformance';
import MockupLogsConversion from './MockupLogsConversion';
import MockupLogsErrorFormats from './MockupLogsErrorFormats';
import '../../styles/mockupLogsDashboard.css';

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function initialFilter(pickerMaxDate: string, sp: URLSearchParams): IFilterState {
  const pickerMin = MOCKUP_LOGS_MIN_DATE;
  const r = readFilterFromSearch(sp);
  const hasRangeInUrl = !!(r.start && r.end);

  let start: string;
  let end: string;
  let preset: string;

  if (hasRangeInUrl) {
    start = r.start!;
    end = r.end!;
    preset = r.preset || 'custom';
  } else {
    ({ start, end, preset } = default7dFilter(pickerMaxDate));
  }

  ({ start, end } = clampFilterRange(start, end, pickerMin, pickerMaxDate));

  let compare = !!r.compare;
  let cstart = r.cstart || '';
  let cend = r.cend || '';
  if (compare && (!cstart || !cend)) {
    const pp = prevPeriod(start, end, pickerMin);
    cstart = pp.cstart;
    cend = pp.cend;
  } else if (compare && cstart && cend) {
    ({ start: cstart, end: cend } = clampFilterRange(cstart, cend, pickerMin, pickerMaxDate));
  }

  return { start, end, compare, cstart, cend, preset };
}

type DashboardTab = 'performance' | 'conversion';

const fmtCount = (n: number) => n.toLocaleString('en-US');

/**
 * `/mockup-logs`: daily session and event health from `/mockup/stats/logs`, aggregated over the selected
 * range and (optionally) compared with the period before it. Charts are canvas-drawn (`lib/.../charts.ts`).
 */
const MockupLogsDashboard: React.FC = () => {
  const theme = useTheme();
  const themeMode = theme.palette.mode;
  const palette = useMemo(() => dashboardPalette(theme.custom), [theme]);
  const cssVars = useMemo(() => dashboardCssVars(theme.custom) as React.CSSProperties, [theme]);
  const [searchParams, setSearchParams] = useSearchParams();
  const pickerMaxDate = useMemo(() => todayIso(), []);
  const [dailyByDate, setDailyByDate] = useState<Record<string, IDailyStat>>({});
  const [filter, setFilter] = useState<IFilterState>(() => initialFilter(pickerMaxDate, searchParams));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<DashboardTab>('conversion');
  const [rawOpen, setRawOpen] = useState(false);
  const [resizeTick, setResizeTick] = useState(0);
  const fetchGenRef = useRef(0);
  const dailyByDateRef = useRef(dailyByDate);
  dailyByDateRef.current = dailyByDate;

  const dailyCatalog = useMemo(() => cacheToSortedArray(dailyByDate), [dailyByDate]);
  const pickerMinDate = MOCKUP_LOGS_MIN_DATE;

  const loadForFilter = useCallback(async (f: IFilterState) => {
    if (!f.start || !f.end) return;

    const gen = ++fetchGenRef.current;
    const isEmpty = Object.keys(dailyByDateRef.current).length === 0;
    if (isEmpty) setLoading(true);
    else setRefreshing(true);
    setError(null);

    try {
      const merged = await fetchMissingForFilter(dailyByDateRef.current, f);
      if (fetchGenRef.current !== gen) return;
      setDailyByDate(merged);
    } catch (err: unknown) {
      if (fetchGenRef.current !== gen) return;
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      if (fetchGenRef.current === gen) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    setSearchParams(new URLSearchParams(writeFilterToSearch(filter)), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL once on mount
  }, []);

  useEffect(() => {
    loadForFilter(filter);
  }, [filter.start, filter.end, filter.compare, filter.cstart, filter.cend, loadForFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFilterChange = useCallback(
    (next: IFilterState) => {
      // Clear cache so every manual filter change fetches fresh data from the server
      setDailyByDate({});
      setFilter(next);
      setSearchParams(new URLSearchParams(writeFilterToSearch(next)), { replace: true });
    },
    [setSearchParams]
  );

  const curr = useMemo(() => {
    if (!filter.start || !filter.end || !dailyCatalog.length) return null;
    return aggregate(dailyCatalog, filter.start, filter.end);
  }, [dailyCatalog, filter.start, filter.end]);

  const prev = useMemo(() => {
    if (!filter.compare || !filter.cstart || !filter.cend || !dailyCatalog.length) return null;
    return aggregate(dailyCatalog, filter.cstart, filter.cend);
  }, [dailyCatalog, filter.compare, filter.cstart, filter.cend]);

  useEffect(() => {
    let t: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => setResizeTick((x) => x + 1), 80);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, []);

  const rawJson = useMemo(() => {
    if (!curr) return '';
    return JSON.stringify({ current: curr, comparison: prev }, null, 2);
  }, [curr, prev]);

  const busy = loading || refreshing;
  const unit = tab === 'performance' ? 'events' : 'sessions';
  const volume = (agg: NonNullable<typeof curr>) => (tab === 'performance' ? agg.eventStats.eventCount : agg.sessionStats.totalSessions);

  let body: React.ReactNode;
  if (loading && !dailyCatalog.length) {
    body = <p className="status-line" role="status">Loading mockup stats…</p>;
  } else if (error && !dailyCatalog.length) {
    body = (
      <Panel>
        <EmptyState title="The stats could not be loaded" hint={error} action={<Button variant="outlined" onClick={() => loadForFilter(filter)}>Retry</Button>} />
      </Panel>
    );
  } else if (!curr) {
    body = (
      <Panel>
        <EmptyState title="No daily data for this range" hint={refreshing ? 'Loading…' : 'Pick another range; data starts on 30 Jul 2024.'} />
      </Panel>
    );
  } else {
    body = (
      <>
        {refreshing && <p className="status-line" role="status">Updating data…</p>}
        {error && <p className="status-line error" role="alert">{error}</p>}
        {prev && (
          <div className="compare-strip" role="note">
            <span>
              <StatusChip tone="brand" size="sm" dot>Current</StatusChip>{' '}
              <strong>{filter.start} → {filter.end}</strong> · {fmtCount(volume(curr))} {unit}
            </span>
            <span>
              <StatusChip tone="neutral" size="sm" dot>Compared</StatusChip>{' '}
              <strong>{filter.cstart} → {filter.cend}</strong> · {fmtCount(volume(prev))} {unit}
            </span>
          </div>
        )}
        {tab === 'performance' ? (
          <>
            <MockupLogsPerformance curr={curr} prev={prev} resizeTick={resizeTick} themeMode={themeMode} palette={palette} />
            <MockupLogsErrorFormats
              start={filter.start}
              end={filter.end}
              cstart={filter.compare ? filter.cstart : ''}
              cend={filter.compare ? filter.cend : ''}
              resizeTick={resizeTick}
              themeMode={themeMode}
              palette={palette}
            />
          </>
        ) : (
          <MockupLogsConversion curr={curr} prev={prev} resizeTick={resizeTick} themeMode={themeMode} palette={palette} />
        )}
      </>
    );
  }

  return (
    <div className="mockupLogsDashboard" style={cssVars}>
      <PageHeader
        eyebrow="Mockups"
        title="Mockup logs"
        subtitle="Daily health and conversion of the mockup pipeline; compare any range with the period before it."
        actions={curr ? (
          <Button variant="outlined" size="small" startIcon={<DataObjectOutlinedIcon />} onClick={() => setRawOpen(true)}>
            Raw data
          </Button>
        ) : undefined}
      >
        <Tabs value={tab} onChange={(_, value: DashboardTab) => setTab(value)} aria-label="Dashboard sections">
          <Tab value="conversion" label="Conversion" id="mockup-logs-tab-conversion" aria-controls="mockup-logs-panel" />
          <Tab value="performance" label="Performance" id="mockup-logs-tab-performance" aria-controls="mockup-logs-panel" />
        </Tabs>
      </PageHeader>
      <Panel padding="none">
        <MockupLogsFilterBar
          minDate={pickerMinDate}
          maxDate={pickerMaxDate}
          state={filter}
          onChange={onFilterChange}
          disabled={busy}
        />
      </Panel>
      <div className="app" role="tabpanel" id="mockup-logs-panel" aria-labelledby={`mockup-logs-tab-${tab}`}>{body}</div>
      <AppDialog
        open={rawOpen}
        onClose={() => setRawOpen(false)}
        title="Raw data (current selection)"
        maxWidth="md"
        actions={<Button variant="outlined" onClick={() => setRawOpen(false)}>Close</Button>}
      >
        <Box
          component="pre"
          sx={{
            m: 0,
            background: theme.custom.color.surfaceMuted,
            border: `1px solid ${theme.custom.color.border}`,
            borderRadius: 2,
            p: 2,
            fontSize: 12,
            fontFamily: theme.custom.font.mono,
            maxHeight: 500,
            overflow: 'auto',
            color: theme.palette.text.primary,
          }}
        >
          {rawJson}
        </Box>
      </AppDialog>
    </div>
  );
};

export default MockupLogsDashboard;
